import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EstimatorController, type RawAnswerBag } from "@reno-estimator/ui";
import { commonQuestions, findStaleAnswerIds, getQuestionSetForProjectType } from "@reno-estimator/config";
import type { EstimatorInput, EstimatorResult, ProjectType } from "@reno-estimator/core";
import { getEstimatorClient } from "../engine/estimatorClient.js";
import { getAnalyticsClient } from "../engine/analyticsClient.js";
import { defaultRoomSqft } from "../lib/defaults.js";

export const STEP_IDS = [
  "project-type",
  "room-size",
  "changes",
  "finish",
  "location",
  "timeline",
  "estimate"
] as const;

export type StepId = (typeof STEP_IDS)[number];

export const STEP_TITLES: Record<StepId, string> = {
  "project-type": "What are you renovating?",
  "room-size": "How large is the space?",
  changes: "What are you changing?",
  finish: "What level of finish are you considering?",
  location: "Where is the project located?",
  timeline: "When are you hoping to start?",
  estimate: "Your estimate"
};

type ResultState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; data: EstimatorResult; input: EstimatorInput }
  | { status: "error"; message: string };

const COMMON_QUESTION_IDS = new Set(commonQuestions.map((q) => q.id));

function makeRequestId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `req-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Owns the question-flow state for the whole app: which step we're on,
 * the answer bag (delegated to @reno-estimator/ui's EstimatorController,
 * which is itself framework-agnostic and has no pricing logic), and the
 * most recent calculated result. Every dollar figure ultimately comes
 * from EstimatorController.submit() -> EstimatorClient -> the real
 * calculation engine; nothing in this hook computes a price itself.
 *
 * State management contract (see docs/estimator-ui.md):
 *  - Switching project type carries forward only the common-question
 *    answers (location, scope, finish level, work-type flags, etc.) -
 *    project-specific answers from the previous project type (e.g.
 *    kitchen_cabinetry) are dropped, since they don't apply to the new
 *    type and would otherwise sit as stale, invisible data forever.
 *  - Any answer change re-evaluates every question's `displayIf` against
 *    the updated answers and clears any answer whose question is no
 *    longer displayed (e.g. answering "no" to accessibility clears
 *    accessibility_notes; changing kitchen layout away from
 *    "reconfiguration" clears the island answer). This is generic -
 *    driven by @reno-estimator/config's question definitions, not
 *    hand-written per field - so it applies automatically to any new
 *    project type's conditional questions too.
 *  - Any answer change (including a dependent-answer clear) invalidates
 *    the last computed result until the next calculate() call, so a
 *    changed answer can never be reflected by a stale estimate.
 */
export interface UseEstimatorFlowOptions {
  /**
   * When set, the flow pre-selects this project type and jumps straight to
   * the room-size step on first mount - the same state a manual click on
   * step 1 would produce for that project type, just applied automatically.
   * Used by the WordPress embed's `project="kitchen"` shortcode/block
   * attribute (see packages/wordpress) so a dedicated landing page can open
   * directly into the relevant estimator. Optional and unused by the
   * standalone app, which keeps its existing step-1-first behavior.
   */
  initialProjectType?: ProjectType;
}

export function useEstimatorFlow(options: UseEstimatorFlowOptions = {}) {
  const { initialProjectType } = options;
  const [projectType, setProjectTypeState] = useState<ProjectType | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswersState] = useState<RawAnswerBag>({});
  const [result, setResult] = useState<ResultState>({ status: "idle" });

  const client = useMemo(() => getEstimatorClient(), []);
  const analytics = useMemo(() => getAnalyticsClient(), []);
  const controllerRef = useRef<EstimatorController | null>(null);
  const controllerProjectTypeRef = useRef<ProjectType | null>(null);
  const appliedInitialProjectType = useRef(false);
  /** Set once the estimate has actually been generated, so the abandonment-tracking effect below never fires for someone who reached their result - see docs/privacy-security.md's analytics event catalog. */
  const reachedEstimateRef = useRef(false);
  const openedTrackedRef = useRef(false);
  const abandonedTrackedRef = useRef(false);

  function ensureController(pt: ProjectType): EstimatorController {
    if (!controllerRef.current || controllerProjectTypeRef.current !== pt) {
      const previousAnswers = controllerRef.current ? controllerRef.current.getAnswers() : {};
      const next = new EstimatorController(pt, client, makeRequestId);
      for (const [key, value] of Object.entries(previousAnswers)) {
        // Only common-question answers survive a project type switch - a
        // project-specific answer from the old type (e.g. kitchen_island)
        // has no meaning for the new one and must not leak into its
        // rawAnswers (see packages/core/src/calculation/explanation.ts,
        // which also guards against this defensively).
        if (COMMON_QUESTION_IDS.has(key)) next.setAnswer(key, value);
      }
      controllerRef.current = next;
      controllerProjectTypeRef.current = pt;
    }
    return controllerRef.current;
  }

  /** Clears any answer whose controlling question is no longer visible given the current answers, for the given project type. Returns the resulting answer bag. */
  function pruneStaleAnswers(controller: EstimatorController, pt: ProjectType): RawAnswerBag {
    const questions = getQuestionSetForProjectType(pt).questions;
    let current = controller.getAnswers();
    // Repeat until stable: clearing one answer can itself make another
    // question's displayIf condition false (a short dependency chain).
    for (let i = 0; i < 5; i++) {
      const stale = findStaleAnswerIds(questions, current);
      if (stale.length === 0) break;
      for (const id of stale) controller.clearAnswer(id);
      current = controller.getAnswers();
    }
    return current;
  }

  const selectProjectType = useCallback(
    (pt: ProjectType) => {
      const controller = ensureController(pt);
      if (controller.getAnswer("room_size") === undefined) {
        controller.setAnswer("room_size", { value: defaultRoomSqft(pt), unit: "sqft" });
      }
      const pruned = pruneStaleAnswers(controller, pt);
      setProjectTypeState(pt);
      setAnswersState(pruned);
      setResult({ status: "idle" });
      analytics.track("project_type_selected", { projectType: pt });
    },
    [client, analytics]
  );

  const setAnswer = useCallback(
    (questionId: string, value: unknown) => {
      if (!projectType) return;
      const controller = ensureController(projectType);
      controller.setAnswer(questionId, value);
      const pruned = pruneStaleAnswers(controller, projectType);
      setAnswersState(pruned);
      // Any answer change (including a dependent-answer clear) invalidates the last computed estimate until recalculated.
      setResult((prev) => (prev.status === "ready" ? { status: "idle" } : prev));
    },
    [projectType]
  );

  // Apply a caller-supplied initial project type exactly once, on first
  // mount - mirrors what selectProjectType(pt) + goToStep(1) already do for
  // a manual selection, so no new state-transition logic is introduced.
  useEffect(() => {
    if (initialProjectType && !appliedInitialProjectType.current) {
      appliedInitialProjectType.current = true;
      selectProjectType(initialProjectType);
      setStepIndex(1);
    }
  }, [initialProjectType, selectProjectType]);

  const getAnswer = useCallback((questionId: string) => answers[questionId], [answers]);

  const calculate = useCallback(async () => {
    if (!projectType) return;
    const controller = ensureController(projectType);
    setResult({ status: "loading" });
    try {
      const data = await controller.submit();
      const input = controller.getLastInput();
      if (!input) throw new Error("Estimator controller did not record the input it just submitted.");
      setResult({ status: "ready", data, input });
      reachedEstimateRef.current = true;
      analytics.track("estimate_generated", { projectType }, data.requestId);
    } catch (err) {
      setResult({ status: "error", message: err instanceof Error ? err.message : "Something went wrong while calculating your estimate." });
    }
  }, [projectType, analytics]);

  const goToStep = useCallback((index: number) => {
    setStepIndex(Math.max(0, Math.min(STEP_IDS.length - 1, index)));
  }, []);

  const goNext = useCallback(() => {
    setStepIndex((i) => {
      analytics.track("step_completed", { step: STEP_IDS[i]! });
      return Math.min(STEP_IDS.length - 1, i + 1);
    });
  }, [analytics]);

  const goBack = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const currentStepId: StepId = STEP_IDS[stepIndex]!;

  // "Estimator opened" - fired exactly once per mount of this hook (i.e.
  // once per page load of the app), not once per step.
  useEffect(() => {
    if (openedTrackedRef.current) return;
    openedTrackedRef.current = true;
    analytics.track("estimator_opened");
  }, [analytics]);

  // "Estimator abandoned" - fired at most once, only if the visitor
  // leaves (closes the tab, navigates away, backgrounds the page) before
  // ever reaching a generated estimate. Someone who sees their estimate
  // and simply doesn't submit the lead form is not "abandoned" for this
  // event's purposes - that's what quote_request_started existing
  // without a matching quote_request_submitted already tells us.
  useEffect(() => {
    function handlePageHide() {
      if (reachedEstimateRef.current || abandonedTrackedRef.current) return;
      abandonedTrackedRef.current = true;
      analytics.track("estimator_abandoned", { abandonmentStep: currentStepId });
    }
    window.addEventListener("pagehide", handlePageHide);
    return () => window.removeEventListener("pagehide", handlePageHide);
  }, [analytics, currentStepId]);

  return {
    stepIndex,
    currentStepId,
    totalSteps: STEP_IDS.length,
    goToStep,
    goNext,
    goBack,
    projectType,
    selectProjectType,
    answers,
    setAnswer,
    getAnswer,
    result,
    calculate
  };
}

export type EstimatorFlow = ReturnType<typeof useEstimatorFlow>;
